import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface VideoProcessingStatus {
  id: string;
  status: string;
  isProcessing: boolean;
  isReady: boolean;
  /**
   * TRUE ONLY when Bunny genuinely failed the encode (Bunny status 5 or 8).
   * It must never mean "we could not read the status" — see `checkFailed`.
   */
  hasError: boolean;
  /**
   * KAN-532: we could not READ the status (network blip, 5xx, timeout). The
   * video is almost certainly fine; nothing about it is known either way.
   * Consumers must keep polling and must NOT surface a failure or write
   * `status: 'failed'` to the row.
   *
   * Why the two were split: on 2026-09-09 the reporter's video was polled
   * successfully 57 times over 11 minutes (last poll 12:36:30Z, 218ms, no
   * error) and then a client PUT marked it failed at 12:36:43Z. Bunny's status
   * at that moment was 0 (Queued); the backend only sets hasError on status 5.
   * The `hasError` the client acted on came from this service's own catchError.
   * The video finished encoding at 12:52:57Z and is `ready` today.
   */
  checkFailed?: boolean;
  statusMessage: string;
  encodeProgress?: number;
  // Additional fields from VideoStatus interface
  thumbnailUrl?: string;
  title?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BunnyVideoStatusService {
  private processingVideos = new BehaviorSubject<{[key: string]: VideoProcessingStatus}>({});
  
  constructor(private http: HttpClient) {}

  /**
   * Check the processing status of a Bunny Stream video
   * @param videoId Bunny Stream video ID
   */
  checkVideoStatus(videoId: string): Observable<VideoProcessingStatus> {
    if (!videoId || !videoId.match(/^[a-f0-9\-]{36}$/i)) {
      // KAN-507: an unusable id can never resolve to a playable video. This
      // used to answer `isReady: true`, which flipped the card badge green for
      // a video that would never play.
      return of({
        id: videoId || 'unknown',
        status: 'unknown',
        isProcessing: false,
        isReady: false,
        hasError: true,
        statusMessage: 'Invalid video ID'
      });
    }

    return this.http.get<any>(`${environment.apiUrl}/videos/${videoId}/status`)
      .pipe(
        map(response => {
          // Unwrap the response if it's wrapped in { success, data, timestamp }
          if (response.success && response.data) {
            return response.data as VideoProcessingStatus;
          }
          return response as VideoProcessingStatus;
        }),
        tap(status => {
          const currentVideos = this.processingVideos.value;
          this.processingVideos.next({
            ...currentVideos,
            [videoId]: status
          });
        }),
        catchError(error => {
          console.error('[BunnyVideoStatusService] Error checking video status:', error);
          // KAN-532: a failed READ is not a failed ENCODE. This used to emit
          // `hasError: true`, which every consumer read as "Bunny failed" —
          // showing "Upload Failed - Video processing failed on Bunny CDN" and
          // writing `status: 'failed'` onto a row that was encoding fine.
          return of({
            id: videoId,
            status: 'check-failed',
            isProcessing: true,
            isReady: false,
            hasError: false,
            checkFailed: true,
            statusMessage: 'Could not fetch video status'
          });
        })
      );
  }

  /**
   * Get the current status of all tracked videos
   */
  getProcessingVideos(): Observable<{[key: string]: VideoProcessingStatus}> {
    return this.processingVideos.asObservable();
  }

  /**
   * Extract video ID from a Bunny Stream URL
   * @param url The URL to extract from
   */
  extractVideoId(url: string): string | null {
    if (!url) return null;
    
    // If it's already just an ID (UUID format)
    if (url.match(/^[a-f0-9\-]{36}$/i)) {
      return url;
    }
    
    // Try to extract from various URL formats
    const videoIdMatch = url.match(/([a-f0-9\-]{36})/i);
    if (videoIdMatch && videoIdMatch[1]) {
      return videoIdMatch[1];
    }
    
    return null;
  }
}

// Alias for backward compatibility and easier imports
export { BunnyVideoStatusService as VideoStatusService };
